import { useMemo } from 'react';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  TableLoading,
} from '@/components/ui/table';
import { Pagination } from '@/components/ui/pagination';
import { StatusBadge, type StatusType } from '@/components/ui/status-badge';
import { ServiceIcon } from '@/components/ui/service-icon';
import { useDIDs } from '../hooks';
import { useDIDStore } from '../stores/did-store';
import { formatPhoneNumber } from '@/lib/utils';
import {
  Search,
  Phone,
  PhoneForwarded,
  ShieldAlert,
  Mail,
  Users,
  CircleUser,
  BadgeCheck,
  MapPin,
  Bell,
  MessageSquare,
  X,
} from 'lucide-react';
import { DisconnectDIDModal } from './disconnect-did-modal';
import type { DID } from '../types';

export function DIDList() {
  const { data: dids, isLoading, error } = useDIDs();
  const {
    searchQuery,
    serviceFilter,
    currentPage,
    pageSize,
    staleFilterActive,
    staleDIDsList,
    setSearchQuery,
    setServiceFilter,
    openDetailModal,
    setCurrentPage,
    setPageSize,
    clearStaleFilter,
  } = useDIDStore();

  // Filter and search DIDs
  const filteredDIDs = useMemo(() => {
    if (!dids) return [];

    let result = [...dids];

    // Apply stale filter (DIDs with no traffic)
    if (staleFilterActive && staleDIDsList.length > 0) {
      const staleSet = new Set(staleDIDsList);
      result = result.filter((did) => staleSet.has(did.tn));
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (did) =>
          did.tn.includes(query) ||
          did.cnam_name?.toLowerCase().includes(query) ||
          did.state?.toLowerCase().includes(query) ||
          did.rate_center?.toLowerCase().includes(query)
      );
    }

    // Apply service filter
    if (serviceFilter) {
      result = result.filter((did) => {
        switch (serviceFilter) {
          case 'cnam': return did.cnam;
          case 'callerId': return did.has_caller_id;
          case 'sms': return did.sms;
          case 'e911': return did.has_911;
          case 'forward': return did.has_forward;
          case 'failover': return did.has_failover;
          case 'efax': return did.has_fax_to_email;
          case 'conference': return did.has_conferencing;
          default: return true;
        }
      });
    }

    return result;
  }, [dids, searchQuery, serviceFilter, staleFilterActive, staleDIDsList]);

  // Paginate
  const totalItems = filteredDIDs.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedDIDs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredDIDs.slice(start, start + pageSize);
  }, [filteredDIDs, currentPage, pageSize]);

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        Error loading DIDs. Please try again.
      </div>
    );
  }

  return (
    <div>
      {/* Search and Filter */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            placeholder="Search DIDs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Filter:</span>
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">All Services</option>
            <option value="forward">Forwarding</option>
            <option value="failover">Failover</option>
            <option value="efax">eFax</option>
            <option value="conference">Conference</option>
            <option value="e911">Emergency 911</option>
            <option value="callerId">Caller ID</option>
            <option value="sms">SMS</option>
            <option value="cnam">CNAM</option>
          </select>
        </div>
        {/* Stale Filter Chip */}
        {staleFilterActive && (
          <button
            onClick={clearStaleFilter}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
          >
            Stale DIDs ({staleDIDsList.length})
            <X size={14} className="text-purple-500" />
          </button>
        )}
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="px-6 w-64">NUMBER</TableHead>
            <TableHead className="px-4 w-16">STATUS</TableHead>
            <TableHead className="px-4 w-72">SERVICES</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableLoading colSpan={3} rows={5} />
          ) : paginatedDIDs.length === 0 ? (
            <TableEmpty
              colSpan={3}
              message={searchQuery ? 'No DIDs match your search' : 'No DIDs found'}
              icon={<Phone className="h-8 w-8 text-slate-300" />}
            />
          ) : (
            paginatedDIDs.map((did) => (
              <DIDRow
                key={did.tn}
                did={did}
                onClick={() => openDetailModal(did.tn)}
              />
            ))
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        className="border-t border-slate-100"
      />

      {/* Disconnect DID Modal */}
      <DisconnectDIDModal />
    </div>
  );
}

// Individual DID row component
interface DIDRowProps {
  did: DID;
  onClick: () => void;
}

function DIDRow({ did, onClick }: DIDRowProps) {
  // Determine status
  const getStatus = (): StatusType => {
    if (did.status === 'suspended') return 'suspended';
    if (did.status === 'pending') return 'pending';
    return 'active';
  };

  // Format location
  const location = [did.rate_center, did.state].filter(Boolean).join(', ') || 'Unknown';

  return (
    <TableRow
      className="cursor-pointer hover:bg-slate-50/80 transition-colors"
      onClick={onClick}
    >
      {/* NUMBER */}
      <TableCell className="px-6">
        <div>
          <span className="font-mono-data text-indigo-600 font-medium">
            {formatPhoneNumber(did.tn)}
          </span>
          <p className="text-xs text-slate-400 mt-0.5">{location}</p>
        </div>
      </TableCell>

      {/* STATUS */}
      <TableCell className="px-4">
        <StatusBadge status={getStatus()} />
      </TableCell>

      {/* SERVICES */}
      <TableCell className="px-4">
        <div className="flex items-center gap-x-4 px-1">
          <ServiceIcon
            icon={ShieldAlert}
            active={!!did.has_failover}
            title="Failover"
            size="lg"
          />
          <ServiceIcon
            icon={PhoneForwarded}
            active={!!did.has_forward}
            title="Forward"
            size="lg"
          />
          <ServiceIcon
            icon={Mail}
            active={!!did.has_fax_to_email}
            title="eFax"
            size="lg"
          />
          <ServiceIcon
            icon={Users}
            active={!!did.has_conferencing}
            title="Conference"
            size="lg"
          />
          <ServiceIcon
            icon={CircleUser}
            active={!!did.has_caller_id}
            title="Caller ID"
            size="lg"
          />
          <ServiceIcon
            icon={BadgeCheck}
            active={!!did.cnam}
            title="CNAM"
            size="lg"
          />
          <ServiceIcon
            icon={MapPin}
            active={!!did.has_911}
            title="911"
            size="lg"
          />
          <ServiceIcon
            icon={Bell}
            active={!!did.e911_has_alerts}
            title="Alerts"
            size="lg"
          />
          <ServiceIcon
            icon={MessageSquare}
            active={!!did.sms}
            title="SMS"
            size="lg"
          />
        </div>
      </TableCell>
    </TableRow>
  );
}
